"use client";

import { useMemo } from "react";
import { useUser } from "@clerk/nextjs";
import { Loader2, Trophy } from "lucide-react";
import { BadgeSummary } from "@/components/BadgeComponents";
import GuestDashboardPreview from "./GuestDashboardPreview";
import DashboardCard from "./dashboard/DashboardCard";
import DashboardTodaySchedule from "./dashboard/DashboardTodaySchedule";
import DashboardPass from "./dashboard/DashboardPass";
import DashboardRecentNote from "./dashboard/DashboardRecentNote";
import DashboardRecentErrorNote from "./dashboard/DashboardRecentErrorNote";
import DashboardStudyProgress from "./dashboard/DashboardStudyProgress";
import DashboardAccuracy from "./dashboard/DashboardAccuracy";
import DashboardStreak from "./dashboard/DashboardStreak";
import DashboardDday from "./dashboard/DashboardDday";
import DashboardPdfUpdates from "./dashboard/DashboardPdfUpdates";
import PreviousSessionButton from "./dashboard/PreviousSessionButton";
import { useAllProblemHistories } from "./dashboard/useAllProblemHistories";

/**
 * /my 통합 대시보드 인덱스 (JCG-2073).
 *
 *  - 상단(컴팩트 4): 감자 이용권 · 도전과제 배지 · 최근 필기 1개 · 최근 오답노트 1개
 *  - 하단(2열): 공부 진행상황 전체현황 ↔ 일자별 정답률 변화
 *
 * 풀이 이력(getAllProblemHistories)은 무거우므로 페이지에서 1회만 fetch해 정답률·오답노트 위젯이
 * 공유한다. 인덱스는 우측 사이드바 없는 2컬럼(`isMyDashboardHome`)으로 폭을 최대화한다.
 */
export default function MyDashboardPage() {
  const { isSignedIn, isLoaded } = useUser();
  const {
    histories,
    noteOnlyHistories,
    loading: historiesLoading,
  } = useAllProblemHistories();

  // JCG-3430: 최근 오답노트 위젯만 "노트만 있는 문제"(채점 없이 정답보기 등)를 함께 본다.
  // 정답률 위젯(DashboardAccuracy)은 실제 시도만 세야 하므로 histories 원본을 그대로 받는다.
  const errorNoteHistories = useMemo(
    () => [...histories, ...noteOnlyHistories],
    [histories, noteOnlyHistories],
  );

  if (!isLoaded) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isLoaded && !isSignedIn) {
    return <GuestDashboardPreview />;
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 px-4 sm:space-y-5 md:px-0">
      <h1 className="sr-only">내 메뉴</h1>

      {/*
        "지난 회차 보기" 버튼 — 헤더 검색창 왼쪽 슬롯에 portal(자체 마운트 시 스냅샷 캡처·조회).
        스냅샷이 없거나 헤더 슬롯이 없으면 아무것도 렌더하지 않는다(JCG-3283).
      */}
      <PreviousSessionButton />

      {/*
        1행: 연속 학습일 · 시험 D-day · 요약 PDF (JCG-2310 재배치 — 3등분 스탯 카드)
         - 모바일(base): 세로 스택 / sm+: 균등 3등분(연속학습일이 1/3 폭)
         - 오늘의 학습은 1행 그리드 셀에서 빼고 바로 아래 풀폭 얇은 바로 배치
      */}
      <div className="grid gap-3 sm:grid-cols-3 lg:gap-4">
        <DashboardStreak />
        <DashboardDday />
        <DashboardPdfUpdates />
      </div>

      {/*
        2행: 오늘의 학습 — 풀폭 얇은 가로 바 (JCG-2310)
         space-y 컨테이너의 형제이므로 별도 col-span 없이 자동 풀폭. 구독 계획마다 한 줄.
      */}
      <DashboardTodaySchedule />

      {/* 3행: 이용권 · 도전과제 · 최근 필기 · 최근 오답노트 (컴팩트 4) */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <DashboardPass />
        <DashboardCard icon={Trophy} title="도전과제" href="/my/badges">
          {/* 컴팩트 4-up 모바일(grid-cols-2)에서 폭이 좁으므로 배지가 카드 밖으로 새지 않게 클립 */}
          <div className="overflow-hidden">
            <BadgeSummary maxDisplay={4} />
          </div>
        </DashboardCard>
        <DashboardRecentNote />
        <DashboardRecentErrorNote
          histories={errorNoteHistories}
          loading={historiesLoading}
        />
      </div>

      {/* 4행: 공부 진행상황 전체현황 ↔ 일자별 정답률 변화 */}
      <div className="grid gap-4 lg:grid-cols-2">
        <DashboardStudyProgress />
        <DashboardAccuracy histories={histories} loading={historiesLoading} />
      </div>
    </div>
  );
}
